// Typed clients for the register resources (customers, sites, assets).
import { apiRequest } from "./api";

export interface Customer {
  id: string;
  name: string;
  // Session SC9B: the account status the paged register carries (accountOnStop.ts); absent on an unpaged list.
  accountStatus?: import("./api").AccountStatusDto;
  // Direct customer (default) vs facilities-management intermediary. Routes the ESA
  // variant and the billing policy. Always returned by the API; optional here so the
  // edit mapper can fall back safely.
  clientType?: "direct" | "intermediary";
  // Session AOZ: the office's welcome-pack override, independent of clientType (which keeps deciding
  // billing and the ESA variant). null = follow the client type (direct: yes, intermediary: no);
  // true / false = always / never drafted at contract signing. Decide from it ONLY through
  // resolvesWelcomePackEligible (src/lib/welcomePack.ts), never by reading clientType directly.
  // Optional because an API response from before the column existed carries no such key, which the
  // resolver deliberately reads as "follow the client type".
  receivesWelcomePack?: boolean | null;
  // Contract classification (Session AFM): NON_CONTRACT is a one-off / reactive caller
  // billed at double Schedule of Fees rates with no tier, hidden from the default
  // customer list until promoted. isOneOff marks a record created inline during the
  // call-out report flow.
  customerType?: "CONTRACT" | "NON_CONTRACT";
  isOneOff?: boolean;
  billingContact: string | null;
  billingEmail: string | null;
  phone: string | null;
  // Optional second contact (e.g. a building director). When secondaryContactEmail is
  // set it is CC'd on every customer-facing outbound email.
  secondaryContactName: string | null;
  secondaryContactRole: string | null;
  secondaryContactEmail: string | null;
  secondaryContactPhone: string | null;
  // Latest NPS recommend score (0 to 10) captured from a call-out survey reply, and when.
  latestNpsScore?: number | null;
  latestNpsScoredAt?: string | null;
  // Communication preferences (informational). preferredChannel: how the customer prefers
  // to be reached; whatsappNumber: kept for future inbound WhatsApp (not used for sending);
  // marketingOptIn + the date it was given (derived server-side from the flag flip).
  preferredChannel?: "email" | "phone" | "whatsapp";
  whatsappNumber?: string | null;
  marketingOptIn?: boolean;
  marketingOptInAt?: string | null;
  notes: string | null;
  // Session AUH (Phase 7, item 3b): the customer's own payment terms in days (null = ELE's standard terms), the due date of
  // every Xero draft raised for them; and the two chase flags (never chase them; never chase them firmly). Optional so an
  // older response reads as "standard terms, chase normally".
  paymentTermsDays?: number | null;
  doNotChase?: boolean;
  doNotChaseHard?: boolean;
  // Soft-delete marker. Set when the customer has been archived (retained for its
  // signed-contract / authorised-invoice history); null for an active customer.
  archivedAt?: string | null;
  _count?: { sites: number; contracts: number };
  // The customer's own sites, name-ordered (Session AQS, item C-2). GET /api/customers/:id has
  // always included these (customer.routes.ts include: { sites: ... }); only the type and the
  // detail page were behind the server. Absent on the list endpoint, which never includes it.
  sites?: Site[];
}

export interface Site {
  id: string;
  customerId: string;
  name: string;
  address: string;
  postcode: string | null;
  status: string;
  accessDetails: string | null;
  notes: string | null;
  customer?: { id: string; name: string };
  _count?: { assets: number; contracts: number };
  // Default engineer for this site's PPM visits (Session ARA, S-4; built to D84's standing
  // RECOMMENDATION, not a confirmed Director ruling). Optional so older fixtures stay valid.
  defaultEngineerId?: string | null;
  // Session ATZ (Item 3): other names the site is known by on a property platform (Dwellant), so a work order matches it.
  aliases?: string[];
}

export interface Asset {
  id: string;
  siteId: string;
  eleNumber: string | null;
  legacyEleNumber?: string | null;
  assetClass: string;
  liftType: string | null;
  frequency: string | null;
  // Care equipment (Session ACR). peopleLifting: the kit lifts/raises a person, so it carries
  // the 6-monthly LOLER examination requirement (equipment only; lifts leave it false).
  // retiredAt: the engineers-pattern soft deactivation for equipment with completed history
  // (kept, off the schedule, reactivatable); null/absent = active.
  peopleLifting?: boolean;
  retiredAt?: string | null;
  manufacturer: string | null;
  serial: string | null;
  stops: number | null;
  capacityKg: number | null;
  // Extra specification fields surfaced on the lift detail page (Session AC). Present on the list
  // and single-asset DTOs (the server spreads the raw asset row); editable on the detail page.
  driveSystem?: string | null;
  driveType?: string | null;
  yearOfManufacture?: number | null;
  manualDoors?: boolean | null;
  nfcToken: string | null;
  // When the RFID tag URL was last sent to an engineer to programme on site (null = not sent).
  rfidSentAt?: string | null;
  // Session AMZ (the Guv POP Tag, then "Tapping For Tracking"): the lift's SECOND, INTERNAL tag, sited with the paper log
  // card in the motor room. A separate token space from nfcToken above, so neither URL can resolve
  // the other's page. Optional here because the office asset routes have always spread the whole
  // row, so older callers that never asked for these still typecheck.
  attendanceTapToken?: string | null;
  attendanceTapSentAt?: string | null;
  simNumber: string | null;
  simExpiry: string | null;
  lolerDue: string | null;
  lolerCertUrl: string | null;
  lolerCertExpiry: string | null;
  lolerInspector?: string | null;
  lolerState?: string | null;
  lastLolerDate?: string | null;
  nextServiceDue?: string | null;
  // The date this lift was last serviced. Editable on the register to bootstrap the PPM schedule
  // (the highest-priority anchor); also stamped automatically when a service job is approved.
  lastServicedAt?: string | null;
  // The next service date to display (Session N + AD): the earliest open scheduled PPM visit, else a
  // live projection from the active contract's commencement + cadence, with its colour tone +
  // whether the site has an active contract (all computed server-side for the "Next service due"
  // column). `serviceOverdue` is true when a past visit has been missed, so the status reads red
  // even though `nextScheduledServiceAt` is the next UPCOMING visit.
  nextScheduledServiceAt?: string | null;
  nextServiceTone?: "green" | "amber" | "red" | "none" | "no_contract";
  serviceOverdue?: boolean;
  hasContract?: boolean;
  // Session BDH (D-BDH-7): false when no service visit has ever been recorded on the lift. Read only by the PPM dashboard.
  serviceRecorded?: boolean;
  // The maintenance tier this lift is currently covered on (Session AGD), for the "Maintenance
  // tier" row on the lift detail page. Present on the single-asset detail DTO only (the list DTOs
  // do not compute it); null when the lift is not on an active contract.
  contractTier?: "core" | "care" | "complete" | null;
  // The id of the contract that produced contractTier (Session AQW, item C-4), so the "Maintenance
  // tier" fact can link to it. Present alongside contractTier only (single-asset detail DTO); null
  // whenever contractTier is null, and also whenever the covering contract could not be resolved to
  // one id (there is none to link).
  contractId?: string | null;
  notes: string | null;
  // Compliance traffic light, computed server-side: green (up to date), amber (due
  // within 14 days or an open advisory defect), red (overdue or an open immediate defect).
  complianceStatus?: "green" | "amber" | "red";
  site?: { id: string; name: string; address?: string | null; postcode?: string | null; customer?: { id: string; name: string } };
}

export interface Resource<T> {
  list: (query?: Record<string, string>) => Promise<T[]>;
  // Session SC2C: one PAGE of the register, filtered, searched and sorted on the server, with the TRUE total of every match (limit, offset, sort,
  // dir and q travel in the query). The register pages use this; `list` stays for every picker and one-off read.
  listPage?: (query: Record<string, string>) => Promise<{ items: T[]; total: number }>;
  create: (data: Record<string, unknown>) => Promise<T>;
  update: (id: string, data: Record<string, unknown>) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

function createResource<T>(path: string): Resource<T> {
  return {
    list: (query) =>
      apiRequest<T[]>(`${path}${query ? `?${new URLSearchParams(query).toString()}` : ""}`),
    listPage: (query) => apiRequest<{ items: T[]; total: number }>(`${path}?${new URLSearchParams(query).toString()}`),
    create: (data) => apiRequest<T>(path, { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) => apiRequest<T>(`${path}/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove: (id) => apiRequest<void>(`${path}/${id}`, { method: "DELETE" }),
  };
}

export const customers = createResource<Customer>("/customers");
export const sites = createResource<Site>("/sites");
export const assets = createResource<Asset>("/assets");
